import React, { useState, useEffect, forwardRef, useCallback, useMemo, useImperativeHandle, useRef } from 'react';
import MessageBubble from './MessageBubble';
import ChatHeader from './ChatHeader';
import ChatFooter from './ChatFooter';
import { getToolDisplay, getToolIcon } from '../../utils/toolDisplayMapping';
import './ChatBody.css';
import PersonalDataForm from '../ChatBody/PersonalData';
import { CircularProgress } from "@mui/material";
import MobileCarSearch from './MobileCarSearch';
import { useWebSocketContext } from "../../context/Theme";
import { Box } from "@mui/material";
import DefaultChat from './DefaultChat';
import { isVehicleDetailPage } from '../../utils/urlPatterns';
import { createTranslator, getBrowserLanguage } from '../../utils/i18n';

// Create inline search screen components
const InlineCarSearchScreen = ({ onSendMessage, onClose, onSubmit, isMobile, onActionsChange, t }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [budget, setBudget] = useState('');
  const [fuelType, setFuelType] = useState('');
  const [additionalInfo, setAdditionalInfo] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');

  const handleNext = () => {
    if (currentStep < 3) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  // Auto-advance when fuel type is selected
  const handleFuelTypeSelect = (fuel) => {
    setFuelType(fuel);
    // Auto-advance to next step after a short delay
    setTimeout(() => {
      if (currentStep === 1) {
        setCurrentStep(2);
      }
    }, 300);
  };

  const handleSubmit = () => {
    const searchData = { budget, fuelType, additionalInfo, phoneNumber };
    onSubmit(searchData);
    onClose();
  };

  const isCurrentStepValid = () => {
    switch (currentStep) {
      case 0: return budget.trim() !== '';
      case 1: return fuelType !== '';
      case 2: return true; // Additional info is optional
      case 3: return true; // Phone number is optional
      default: return false;
    }
  };

  // Update actions when step or validation changes
  useEffect(() => {
    if (onActionsChange) {
      onActionsChange({
        canGoBack: currentStep > 0,
        canGoNext: currentStep < 3,
        canSubmit: currentStep === 3,
        canProceed: isCurrentStepValid(),
        onBack: handleBack,
        onNext: handleNext,
        onSubmit: handleSubmit,
        onClose,
        submitText: t('carSearchSubmit')
      });
    }
  }, [currentStep, budget, fuelType, additionalInfo, phoneNumber, t]);

  return (
    <div className="inline-search-screen car-search-inline">
      <div className="inline-search-content">
        <div className="inline-search-header">
          <div className="inline-search-icon">
            🚗
          </div>
          <h2>{t('carSearchTitle')}</h2>
          <p>{t('carSearchSubtitle')}</p>
        </div>

        <div className="inline-search-progress">
          {[0, 1, 2, 3].map((step) => (
            <div
              key={step}
              className={`progress-dot ${step <= currentStep ? 'active' : ''}`}
            />
          ))}
        </div>

        <div className="inline-search-form">
          {currentStep === 0 && (
            <div className="search-step">
              <h3>{t('budgetQuestion')}</h3>
              <div className="budget-input-container">
                <input
                  type="text"
                  value={budget}
                  onChange={(e) => setBudget(e.target.value)}
                  placeholder={t('example25000')}
                  className="inline-search-input"
                />
                <span className="input-suffix">€</span>
              </div>
            </div>
          )}

          {currentStep === 1 && (
            <div className="search-step">
              <h3>{t('fuelQuestion')}</h3>
              <div className="fuel-type-options compact-grid">
                {[
                  { label: t('gasoline'), icon: '⛽' },
                  { label: t('diesel'), icon: '🚛' },
                  { label: t('hybrid'), icon: '🔋' },
                  { label: t('electric'), icon: '⚡' },
                  { label: t('noPreference'), icon: '🤷‍♂️' }
                ].map((fuel) => (
                  <button
                    key={fuel.label}
                    className={`fuel-option compact ${fuelType === fuel.label ? 'selected' : ''}`}
                    onClick={() => handleFuelTypeSelect(fuel.label)}
                  >
                    <span className="fuel-icon">{fuel.icon}</span>
                    <span className="fuel-text">{fuel.label}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {currentStep === 2 && (
            <div className="search-step">
              <h3>{t('additionalPreferencesQuestion')}</h3>
              <textarea
                value={additionalInfo}
                onChange={(e) => setAdditionalInfo(e.target.value)}
                placeholder={t('additionalPreferencesPlaceholder')}
                className="inline-search-textarea"
                rows={4}
              />
            </div>
          )}

          {currentStep === 3 && (
            <div className="search-step">
              <h3>{t('contactQuestion')}</h3>
              <div className="mia-aitomotivelab-form-group">
                <input
                  type="tel"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  placeholder={t('phonePlaceholder')}
                  className="inline-search-input"
                />
                <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.5rem', textAlign: 'center' }}>
                  {t('contactHelpText')}
                </p>
              </div>
            </div>
          )}
        </div>

        <button onClick={onClose} className="inline-search-close">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
            <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
          </svg>
        </button>
      </div>
    </div>
  );
};

const InlineRentalSearchScreen = ({ onSendMessage, onClose, onSubmit, isMobile, onActionsChange, t }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [userType, setUserType] = useState('');
  const [kmPerYear, setKmPerYear] = useState('');
  const [budget, setBudget] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');

  const handleNext = () => {
    if (currentStep < 3) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  // Auto-advance when user type is selected
  const handleUserTypeSelect = (type) => {
    setUserType(type);
    setTimeout(() => {
      if (currentStep === 0) {
        setCurrentStep(1);
      }
    }, 300);
  };

  const handleSubmit = () => {
    const searchData = { userType, kmPerYear, budget, phoneNumber };
    onSubmit(searchData);
    onClose();
  };

  const isCurrentStepValid = () => {
    switch (currentStep) {
      case 0: return userType !== '';
      case 1: return kmPerYear.trim() !== '';
      case 2: return true; // Budget is optional
      case 3: return true; // Phone is optional
      default: return false;
    }
  };

  // Update actions when step or validation changes
  useEffect(() => {
    if (onActionsChange) {
      onActionsChange({
        canGoBack: currentStep > 0,
        canGoNext: currentStep < 3,
        canSubmit: currentStep === 3,
        canProceed: isCurrentStepValid(),
        onBack: handleBack,
        onNext: handleNext,
        onSubmit: handleSubmit,
        onClose,
        submitText: t('rentalSearchSubmit')
      });
    }
  }, [currentStep, userType, kmPerYear, budget, phoneNumber, t]);

  return (
    <div className="inline-search-screen rental-search-inline">
      <div className="inline-search-content">
        <div className="inline-search-header">
          <div className="inline-search-icon">
            🔑
          </div>
          <h2>{t('rentalTitle')}</h2>
          <p>{t('rentalSubtitle')}</p>
        </div>

        <div className="inline-search-progress">
          {[0, 1, 2, 3].map((step) => (
            <div
              key={step}
              className={`progress-dot ${step <= currentStep ? 'active' : ''}`}
            />
          ))}
        </div>

        <div className="inline-search-form">
          {currentStep === 0 && (
            <div className="search-step">
              <h3>{t('privateOrBusinessQuestion')}</h3>
              <div className="fuel-type-options compact-grid">
                {[
                  { label: t('privateUse'), icon: '👤' },
                  { label: t('vatNumber'), icon: '🏢' }
                ].map((type) => (
                  <button
                    key={type.label}
                    className={`fuel-option compact ${userType === type.label ? 'selected' : ''}`}
                    onClick={() => handleUserTypeSelect(type.label)}
                  >
                    <span className="fuel-icon">{type.icon}</span>
                    <span className="fuel-text">{type.label}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {currentStep === 1 && (
            <div className="search-step">
              <h3>{t('yearlyKmQuestion')}</h3>
              <div className="fuel-type-options compact-grid">
                {[
                  { label: t('upTo10kKm'), icon: '🚗' },
                  { label: t('between10k20kKm'), icon: '🚙' },
                  { label: t('between20k30kKm'), icon: '🚐' },
                  { label: t('over30kKm'), icon: '🚛' }
                ].map((km) => (
                  <button
                    key={km.label}
                    className={`fuel-option compact ${kmPerYear === km.label ? 'selected' : ''}`}
                    onClick={() => setKmPerYear(km.label)}
                  >
                    <span className="fuel-icon">{km.icon}</span>
                    <span className="fuel-text">{km.label}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {currentStep === 2 && (
            <div className="search-step">
              <h3>{t('monthlyBudgetQuestion')}</h3>
              <div className="budget-input-container">
                <input
                  type="text"
                  value={budget}
                  onChange={(e) => setBudget(e.target.value)}
                  placeholder={t('example300')}
                  className="inline-search-input"
                />
                <span className="input-suffix">{t('perMonth')}</span>
              </div>
            </div>
          )}

          {currentStep === 3 && (
            <div className="search-step">
              <h3>{t('contactQuestion')}</h3>
              <div className="mia-aitomotivelab-form-group">
                <input
                  type="tel"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  placeholder={t('phonePlaceholder')}
                  className="inline-search-input"
                />
                <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.5rem', textAlign: 'center' }}>
                  {t('contactHelpText')}
                </p>
              </div>
            </div>
          )}
        </div>

        <button onClick={onClose} className="inline-search-close">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
            <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
          </svg>
        </button>
      </div>
    </div>
  );
};

const InlineServiceSearchScreen = ({ onSendMessage, onClose, onSubmit, isMobile, onActionsChange, t }) => {
  const [serviceType, setServiceType] = useState('');
  const [vehicleInfo, setVehicleInfo] = useState('');
  const [preferredDate, setPreferredDate] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [currentStep, setCurrentStep] = useState(0);

  const serviceTypes = [
    { label: t('serviceMaintenance'), icon: '🔧' },
    { label: t('serviceInspection'), icon: '📋' },
    { label: t('serviceRepair'), icon: '🛠️' },
    { label: t('serviceTires'), icon: '🛞' },
    { label: t('serviceRoadside'), icon: '🆘' },
    { label: t('serviceOther'), icon: '💭' }
  ];

  const handleServiceTypeSelect = (service) => {
    setServiceType(service);
    // Auto-advance to next step
    setTimeout(() => {
      setCurrentStep(1);
    }, 300);
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleNext = () => {
    if (currentStep < 2) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleSubmit = () => {
    const searchData = { serviceType, vehicleInfo, preferredDate, phoneNumber };
    onSubmit(searchData);
    onClose();
  };

  const isCurrentStepValid = () => {
    switch (currentStep) {
      case 0: return serviceType !== '';
      case 1: return vehicleInfo.trim() !== '';
      case 2: return true; // Phone number is optional
      default: return false;
    }
  };

  // Update actions when step or form validity changes
  useEffect(() => {
    if (onActionsChange) {
      onActionsChange({
        canGoBack: currentStep > 0,
        canGoNext: currentStep < 2,
        canSubmit: currentStep === 2,
        canProceed: isCurrentStepValid(),
        onBack: handleBack,
        onNext: handleNext,
        onSubmit: handleSubmit,
        onClose,
        submitText: t('serviceSearchSubmit')
      });
    }
  }, [currentStep, serviceType, vehicleInfo, preferredDate, phoneNumber, t]);

  return (
    <div className="inline-search-screen service-search-inline">
      <div className="inline-search-content">
        <div className="inline-search-header">
          <div className="inline-search-icon">
            🔧
          </div>
          <h2>{t('serviceTitle')}</h2>
          <p>{t('serviceSubtitle')}</p>
        </div>

        <div className="inline-search-form">
          {currentStep === 0 && (
            <div className="search-step">
              <h3>{t('whichServiceQuestion')}</h3>
              
              <div className="service-type-options compact-grid">
                {serviceTypes.map((service) => (
                  <button
                    key={service.label}
                    className={`service-option compact ${serviceType === service.label ? 'selected' : ''}`}
                    onClick={() => handleServiceTypeSelect(service.label)}
                  >
                    <span className="service-icon">{service.icon}</span>
                    <span className="service-text">{service.label}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {currentStep === 1 && (
            <div className="search-step">
              <h3>{t('serviceDetailsTitle')}</h3>
              
              <div className="mia-aitomotivelab-form-group">
                <label>{t('vehicleInfoLabel')}</label>
                <input
                  type="text"
                  value={vehicleInfo}
                  onChange={(e) => setVehicleInfo(e.target.value)}
                  placeholder={t('vehicleInfoPlaceholder')}
                  className="inline-search-input"
                />
              </div>

              <div className="mia-aitomotivelab-form-group">
                <label>{t('preferredDateLabel')}</label>
                <input
                  type="date"
                  value={preferredDate}
                  onChange={(e) => setPreferredDate(e.target.value)}
                  className="inline-search-input"
                />
              </div>
            </div>
          )}

          {currentStep === 2 && (
            <div className="search-step">
              <h3>{t('contactQuestion')}</h3>
              <div className="mia-aitomotivelab-form-group">
                <input
                  type="tel"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  placeholder={t('phonePlaceholder')}
                  className="inline-search-input"
                />
                <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.5rem', textAlign: 'center' }}>
                  {t('contactHelpText')}
                </p>
              </div>
            </div>
          )}
        </div>

        <button onClick={onClose} className="inline-search-close">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
            <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
          </svg>
        </button>
      </div>
    </div>
  );
};

const InlinePromotionsSearchScreen = ({ onSendMessage, onClose, onSubmit, isMobile, onActionsChange, t }) => {
  const [promotionType, setPromotionType] = useState('');
  const [budget, setBudget] = useState('');
  const [interests, setInterests] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [currentStep, setCurrentStep] = useState(0);
  
  // Additional fields for Accessori and Permute
  const [carModel, setCarModel] = useState('');
  const [carYear, setCarYear] = useState('');
  const [carKilometers, setCarKilometers] = useState('');

  const promotionTypes = [
    { label: t('promoNewCars'), icon: '✨' },
    { label: t('promoUsedCars'), icon: '🚗' },
    { label: t('promoFinancing'), icon: '💳' },
    { label: t('promoTradeIn'), icon: '🔄' },
    { label: t('promoAccessories'), icon: '🛠️' },
    { label: t('promoAll'), icon: '🎯' }
  ];

  const handlePromotionTypeSelect = (promo) => {
    setPromotionType(promo);
    // Auto-advance to next step
    setTimeout(() => {
      setCurrentStep(1);
    }, 300);
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleNext = () => {
    if (currentStep < 2) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleSubmit = () => {
    const searchData = { 
      promotionType, 
      budget, 
      interests, 
      phoneNumber,
      carModel,
      carYear,
      carKilometers
    };
    onSubmit(searchData);
    onClose();
  };

  const isCurrentStepValid = () => {
    switch (currentStep) {
      case 0: return promotionType !== '';
      case 1: 
        // Different validation based on promotion type
        if (promotionType === t('promoAccessories')) {
          return carModel.trim() !== '';
        } else if (promotionType === t('promoTradeIn')) {
          return carModel.trim() !== '' && carYear.trim() !== '' && carKilometers.trim() !== '';
        } else {
          return true; // Budget and interests are optional for other types
        }
      case 2: return true; // Phone number is optional
      default: return false;
    }
  };

  // Update actions when step or form validity changes
  useEffect(() => {
    if (onActionsChange) {
      onActionsChange({
        canGoBack: currentStep > 0,
        canGoNext: currentStep < 2,
        canSubmit: currentStep === 2,
        canProceed: isCurrentStepValid(),
        onBack: handleBack,
        onNext: handleNext,
        onSubmit: handleSubmit,
        onClose,
        submitText: t('promotionsSearchSubmit')
      });
    }
  }, [currentStep, promotionType, budget, interests, phoneNumber, carModel, carYear, carKilometers, t]);

  return (
    <div className="inline-search-screen promotions-search-inline">
      <div className="inline-search-content">
        <div className="inline-search-header">
          <div className="inline-search-icon">
            🎯
          </div>
          <h2>{t('promotionsTitle')}</h2>
          <p>{t('promotionsSubtitle')}</p>
        </div>

        <div className="inline-search-form">
          {currentStep === 0 && (
            <div className="search-step">
              <h3>{t('offerTypeQuestion')}</h3>
              
              <div className="promotion-type-options compact-grid">
                {promotionTypes.map((promo) => (
                  <button
                    key={promo.label}
                    className={`promotion-option compact ${promotionType === promo.label ? 'selected' : ''}`}
                    onClick={() => handlePromotionTypeSelect(promo.label)}
                  >
                    <span className="promotion-icon">{promo.icon}</span>
                    <span className="promotion-text">{promo.label}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {currentStep === 1 && (
            <div className="search-step">
              {promotionType === t('promoAccessories') ? (
                <>
                  <h3>{t('forWhichCarAccessories')}</h3>
                  <div className="mia-aitomotivelab-form-group">
                    <label>{t('carModelLabel')}</label>
                    <input
                      type="text"
                      value={carModel}
                      onChange={(e) => setCarModel(e.target.value)}
                      placeholder={t('carModelPlaceholder')}
                      className="inline-search-input"
                    />
                  </div>
                </>
              ) : promotionType === t('promoTradeIn') ? (
                <>
                  <h3>{t('tradeInInfoTitle')}</h3>
                  <div className="mia-aitomotivelab-form-group">
                    <label>{t('carModelLabel')}</label>
                    <input
                      type="text"
                      value={carModel}
                      onChange={(e) => setCarModel(e.target.value)}
                      placeholder={t('carModelPlaceholder')}
                      className="inline-search-input"
                    />
                  </div>
                  <div className="mia-aitomotivelab-form-group">
                    <label>{t('registrationYearLabel')}</label>
                    <input
                      type="text"
                      value={carYear}
                      onChange={(e) => setCarYear(e.target.value)}
                      placeholder={t('example2020')}
                      className="inline-search-input"
                    />
                  </div>
                  <div className="mia-aitomotivelab-form-group">
                    <label>{t('mileageLabel')}</label>
                    <input
                      type="text"
                      value={carKilometers}
                      onChange={(e) => setCarKilometers(e.target.value)}
                      placeholder={t('example50000')}
                      className="inline-search-input"
                    />
                  </div>
                </>
              ) : (
                <>
                  <h3>{t('additionalDetailsTitle')}</h3>
                  <div className="mia-aitomotivelab-form-group">
                    <label>{t('budgetInterestLabel')}</label>
                    <input
                      type="text"
                      value={budget}
                      onChange={(e) => setBudget(e.target.value)}
                      placeholder={t('example25000')}
                      className="inline-search-input"
                    />
                  </div>
                  <div className="mia-aitomotivelab-form-group">
                    <label>{t('additionalDetailsLabel')}</label>
                    <textarea
                      value={interests}
                      onChange={(e) => setInterests(e.target.value)}
                      placeholder={t('additionalDetailsPlaceholder')}
                      className="inline-search-textarea"
                      rows={3}
                    />
                  </div>
                </>
              )}
            </div>
          )}

          {currentStep === 2 && (
            <div className="search-step">
              <h3>{t('contactQuestion')}</h3>
              <div className="mia-aitomotivelab-form-group">
                <input
                  type="tel"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  placeholder={t('phonePlaceholder')}
                  className="inline-search-input"
                />
                <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.5rem', textAlign: 'center' }}>
                  {t('contactHelpText')}
                </p>
              </div>
            </div>
          )}
        </div>

        <button onClick={onClose} className="inline-search-close">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
            <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
          </svg>
        </button>
      </div>
    </div>
  );
};

// Inline Default Chat Screen component for "Chatta con noi" CTA
const DefaultChatScreen = ({ onSendMessage, onClose, onSubmit, isMobile, t }) => {
  const [selectedOption, setSelectedOption] = useState('');

  const handleOptionSelect = (value) => {
    setSelectedOption(value);
    
    // Map the options to search types and dispatch appropriate action
    let searchType;
    switch (value) {
      case t('optionFindCar'):
        // Close current screen and open car search
        onClose();
        setTimeout(() => {
          const searchEvent = new CustomEvent('openInlineSearch', {
            detail: { searchType: 'car' }
          });
          window.dispatchEvent(searchEvent);
        }, 100);
        return;
      case t('optionBookWorkshop'):
        // Close current screen and open service search  
        onClose();
        setTimeout(() => {
          const searchEvent = new CustomEvent('openInlineSearch', {
            detail: { searchType: 'service' }
          });
          window.dispatchEvent(searchEvent);
        }, 100);
        return;
      case t('optionRequestInfo'):
        // Submit with a general info message
        onSubmit({
          topic: t('optionRequestInfo'),
          message: t('genericInfoMessage')
        });
        return;
      default:
        onClose();
        return;
    }
  };

  return (
    <div className="inline-search-screen default-chat-inline">
      <div className="inline-search-content">
        <div className="inline-search-header">
          <div className="inline-search-icon">
            💬
          </div>
          <h2>{t('defaultHelpTitle')}</h2>
          <p>{t('defaultHelpSubtitle')}</p>
        </div>

        <div className="inline-search-form">
          <div className="search-step">
            <div className="promotion-type-options compact-grid">
              {[
                { label: t('optionFindCar'), icon: '🚗' },
                { label: t('optionBookWorkshop'), icon: '🔧' },
                { label: t('optionRequestInfo'), icon: 'ℹ️' }
              ].map((option) => (
                <button
                  key={option.label}
                  className={`promotion-option compact ${selectedOption === option.label ? 'selected' : ''}`}
                  onClick={() => handleOptionSelect(option.label)}
                >
                  <span className="promotion-icon">{option.icon}</span>
                  <span className="promotion-text">{option.label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        <button onClick={onClose} className="inline-search-close">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
            <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
          </svg>
        </button>
      </div>
    </div>
  );
};

// Create a branded welcome screen component
const BrandedWelcomeScreen = ({ onSendMessage, isMobile, isHumanControlled, controller, showHumanControlElements, automaticHumanControl, isConnected, isAuthenticated, statusReceived, t }) => {
  const [currentHour, setCurrentHour] = useState(new Date().getHours());
  const [numeroWhatsapp, setNumeroWhatsapp] = useState(null);
  const [welcomeMessage, setWelcomeMessage] = useState("Come posso esserti utile?");
  
  // Preload welcome message from localStorage immediately for faster rendering
  const [isWelcomeReady, setIsWelcomeReady] = useState(true); // Start as ready for immediate display
  
  // Function to check if text contains markdown
  const containsMarkdown = (text) => {
    if (!text) return false;
    // Check for markdown patterns: bold, italic, links
    const markdownRegex = /(\*\*.*?\*\*)|(\*.*?\*)|(\[.*?\]\(.*?\))/;
    return markdownRegex.test(text);
  };
  
  // Function to parse markdown in text
  const parseMarkdown = (text) => {
    if (!text) return '';
    
    // If text doesn't contain markdown, return it as is
    if (!containsMarkdown(text)) return text;
    
    // Parse bold text (**text**)
    let parsedText = text.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    
    // Parse italic text (*text*)
    parsedText = parsedText.replace(/\*(.*?)\*/g, '<em>$1</em>');
    
    // Parse links ([text](url))
    parsedText = parsedText.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>');
    
    return parsedText;
  };
  
  // Update the hour every minute
  useEffect(() => {
    const intervalId = setInterval(() => {
      setCurrentHour(new Date().getHours());
    }, 60000);
    
    return () => clearInterval(intervalId);
  }, []);

  // Get WhatsApp number and welcome message from localStorage
  // Optimized: Load data synchronously for immediate rendering
  useEffect(() => {
    // Batch localStorage access for better performance
    const whatsappNumber = localStorage.getItem("numero_whatsapp");
    const storedWelcomeMessage = localStorage.getItem("welcome_message");
    
    // Set values immediately to avoid loading delays
    setNumeroWhatsapp(whatsappNumber && whatsappNumber !== "null" && whatsappNumber !== "" ? whatsappNumber : null);
    
    if (storedWelcomeMessage && storedWelcomeMessage !== "null" && storedWelcomeMessage !== "") {
      setWelcomeMessage(storedWelcomeMessage);
    }
    
    // Mark welcome screen as ready
    setIsWelcomeReady(true);
  }, []);

  // Function to parse markdown in welcome message
  const parseWelcomeMarkdown = (text) => {
    if (!text) return '';
    
    // Parse bold text (**text**)
    let parsedText = text.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    
    // Parse italic text (*text*)
    parsedText = parsedText.replace(/\*(.*?)\*/g, '<em>$1</em>');
    
    // Parse links ([text](url))
    parsedText = parsedText.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>');
    
    return parsedText;
  };

  // Get time-based greeting
  const getTimeBasedGreeting = () => {
    if (currentHour >= 5 && currentHour < 12) {
      return t('goodMorning');
    } else if (currentHour >= 12 && currentHour < 18) {
      return t('goodAfternoon');
    } else {
      return t('goodEvening');
    }
  };

  const buildContextualWhatsAppMessage = useCallback((currentUrl, miaId) => {
    if (isVehicleDetailPage(currentUrl)) {
      return t('whatsappVdpMessage', { url: currentUrl, miaId });
    }
    return t('whatsappPageMessage', { url: currentUrl, miaId });
  }, [t]);

  const handleWhatsAppClick = () => {
    if (!numeroWhatsapp) {
      return;
    }
    
    // Get chat ID from localStorage
    const chatId = localStorage.getItem("aitomotivelab_personalData_chat_id");
    const miaId = chatId && chatId !== "undefined" && chatId !== "null" ? ` MIA_ID=${chatId}` : '';
    
    // Get current URL
    const currentUrl = window.location.href;
    
    // Determine message based on page type
    const message = buildContextualWhatsAppMessage(currentUrl, miaId);
    
    // Create WhatsApp URL with context-aware message
    const whatsappUrl = `https://wa.me/${numeroWhatsapp}?text=${encodeURIComponent(message)}`;
    
    // Open WhatsApp
    window.open(whatsappUrl, '_blank');
  };

  // Determine when to show avatar icons for automatic human control scenarios
  // This includes:
  // a) When automaticHumanControl is explicitly true (first load scenario)
  // b) When human control is active but no specific controller is assigned (reload scenario)
  const shouldShowAvatarIcons = automaticHumanControl === true || 
                                (isHumanControlled && (!controller || !controller.first_name));

  // Determine which icon to show based on mode
  const renderProfileIcon = () => {
    // PRIORITY 1: Show avatar icons for automatic human control scenarios
    if (shouldShowAvatarIcons) {
      return (
        <div className="avatar-icons-container">
          <div className="profile-icon operator-icon male-avatar">
            <img src="https://files.aitomotivelab.com/Avatar%20del%20rappresentante%20del%20servizio%20clienti.webp" alt="Male Operator" />
          </div>
          <div className="profile-icon operator-icon female-avatar">
            <img src="https://files.aitomotivelab.com/donna%20del%20servizio%20clienti.webp" alt="Female Operator" />
          </div>
        </div>
      );
    }
    
    // Show default AI icon immediately if not ready, to avoid loading delay
    if (!showHumanControlElements) {
      return (
        <div className="profile-icon ai-icon">
          <svg viewBox="0 0 283.46 283.46" fill="none">
            <defs>
              <linearGradient id="aiGradient" x1="0" y1="141.73" x2="283.46" y2="141.73" gradientUnits="userSpaceOnUse">
                <stop offset="0" stopColor="#336cb4"/>
                <stop offset="1" stopColor="#29bbe3"/>
              </linearGradient>
            </defs>
            <path fill="url(#aiGradient)" d="M283.46,283.46h-141.73C63.45,283.46,0,220.01,0,141.73S63.45,0,141.73,0s141.73,63.45,141.73,141.73v141.73Z"/>
            <path fill="#fff" d="M241.07,241.22h-.03l.03,14.91-12.89-7.48-12.93-7.43h-73.51c-54.86,0-99.33-44.47-99.33-99.33s44.47-99.33,99.33-99.33,99.33,44.47,99.33,99.33v99.33Z"/>
            <g>
              <path fill="#0e1423" d="M92.38,130.15c-6.06,0-10.97,4.91-10.97,10.97s4.91,10.97,10.97,10.97,10.97-4.91,10.97-10.97-4.91-10.97-10.97-10.97Z"/>
              <path fill="#0e1423" d="M191.08,130.15c-6.06,0-10.97,4.91-10.97,10.97s4.91,10.97,10.97,10.97,10.97-4.91,10.97-10.97-4.91-10.97-10.97-10.97Z"/>
              <path fill="#0e1423" d="M160.92,141.11h-38.39c-3.03,0-5.54,2.48-5,5.46,2.28,12.53,12.24,21.96,24.19,21.96s21.91-9.43,24.19-21.96c.54-2.98-1.97-5.46-5-5.46Z"/>
            </g>
          </svg>
        </div>
      );
    } 
    
    // PRIORITY 2: If human control is active and we have a specific operator, show operator icon
    else if (isHumanControlled && controller && controller.first_name) {
      return (
        <div className="profile-icon operator-icon">
          <svg viewBox="0 0 100 100" fill="currentColor">
            <path d="M74.1837463,49.6968689c-1.4159546-0.8648071-3.0491333-1.296875-4.7064819-1.3320312   c-2.416626-0.0512085-4.7433472,0.9563599-6.529541,2.5848999c-3.5632935,3.2487793-6.4158936,5.5033569-12.9476929,5.5033569   c-6.5349121,0-9.4993896-2.3184204-12.8956299-5.4764404c-1.5609131-1.4515381-3.539856-2.4508057-5.666748-2.5912476   c-1.9647217-0.1296997-3.9453125,0.2875977-5.6213379,1.3114624c-5.0441284,3.0804443-15.2316895,12.5601807-15.2316895,38.8806763   c0,1.0379028,0.0161743,2.0392456,0.0450439,3.0115967c0.0646362,2.1806641,1.866394,3.9067993,4.0479736,3.9067993h16.0476685   h11.4761963H57.219574h11.081665h17.0209961c2.1815186,0,3.9832153-1.7260132,4.0480347-3.9065552   c0.0288696-0.9724731,0.045105-1.973877,0.045105-3.0118408C89.4153748,62.2570496,79.2276917,52.7773132,74.1837463,49.6968689z"/>
            <path d="M30.7253113,35.335968c1.0706787,0,2.0858765-0.230957,3.0054321-0.6387329   c2.6590576,6.3646851,8.9401855,10.8389282,16.2692871,10.8389282c7.2769165,0,13.5211792-4.4109497,16.2123413-10.7036133   c0.8300781,0.3206177,1.7286987,0.503418,2.671875,0.503418c0.4107666,0,0.8108521-0.0419312,1.2034912-0.1060791   c-2.7969971,7.6467285-9.8185425,13.2632446-18.2370605,13.9890137c-0.3758545-0.6342163-1.0599365-1.0641479-1.850647-1.0641479   c-1.1919556,0-2.1582031,0.9662476-2.1582031,2.1582031s0.9662476,2.1582031,2.1582031,2.1582031   c0.864624,0,1.6049805-0.5117798,1.9494019-1.2459717c9.7877197-0.8117676,17.8813477-7.6681519,20.5310059-16.8195801   c2.2839966-1.2669678,3.8314209-3.7003784,3.8314209-6.4972534c0-2.796936-1.5474243-5.2302856-3.8314209-6.4972534   c-2.8238525-9.7531738-11.829834-16.9070435-22.4806519-16.9070435c-10.5685425,0-19.5189819,7.0429688-22.4153442,16.6806641   c-2.5300903,1.1841431-4.2867432,3.7456055-4.2867432,6.7236328C23.297699,32.0104675,26.6231384,35.335968,30.7253113,35.335968z    M49.9997864,6.5040588c9.2281494,0,17.0879517,5.8807373,20.0880127,14.0827637   c-0.3926392-0.0641479-0.7927856-0.1060791-1.2035522-0.1060791c-0.9431763,0-1.8417969,0.1828003-2.671875,0.503418   C63.5212097,14.6913757,57.276947,10.280426,50.0000305,10.280426c-7.3291016,0-13.6102295,4.4743042-16.2692871,10.8390503   c-0.9194946-0.4077759-1.9347534-0.6387329-3.0054321-0.6387329c-0.2668457,0-0.5299683,0.0154419-0.7894897,0.0429077   C32.9529114,12.3547058,40.7953186,6.5040588,49.9997864,6.5040588z"/>
          </svg>
        </div>
      );
    } 
    
    // PRIORITY 3: If human control is active but automaticHumanControl is explicitly false, show waiting icon
    else if (isHumanControlled && automaticHumanControl === false) {
      return (
        <div className="profile-icon waiting-icon">
          <svg viewBox="0 0 100 100" fill="currentColor">
            <g>
              <circle cx="50" cy="50" r="8" fill="currentColor"/>
              <path d="M50 10v16m0 16v16m35-21H85m-16 0H65m-30 0H19m-9 0h16" stroke="currentColor" strokeWidth="3" strokeLinecap="round"/>
              <circle cx="50" cy="50" r="20" fill="none" stroke="currentColor" strokeWidth="2" strokeDasharray="5,5">
                <animateTransform
                  attributeName="transform"
                  type="rotate"
                  values="0 50 50;360 50 50"
                  dur="2s"
                  repeatCount="indefinite"/>
              </circle>
            </g>
          </svg>
        </div>
      );
    } 
    
    // DEFAULT: Show AI assistant icon
    else {
      // AI assistant is active - show aitomotivelab icon
      return (
        <div className="profile-icon ai-icon">
          <svg viewBox="0 0 283.46 283.46" fill="none">
            <defs>
              <linearGradient id="aiGradient" x1="0" y1="141.73" x2="283.46" y2="141.73" gradientUnits="userSpaceOnUse">
                <stop offset="0" stopColor="#336cb4"/>
                <stop offset="1" stopColor="#29bbe3"/>
              </linearGradient>
            </defs>
            <path fill="url(#aiGradient)" d="M283.46,283.46h-141.73C63.45,283.46,0,220.01,0,141.73S63.45,0,141.73,0s141.73,63.45,141.73,141.73v141.73Z"/>
            <path fill="#fff" d="M241.07,241.22h-.03l.03,14.91-12.89-7.48-12.93-7.43h-73.51c-54.86,0-99.33-44.47-99.33-99.33s44.47-99.33,99.33-99.33,99.33,44.47,99.33,99.33v99.33Z"/>
            <g>
              <path fill="#0e1423" d="M92.38,130.15c-6.06,0-10.97,4.91-10.97,10.97s4.91,10.97,10.97,10.97,10.97-4.91,10.97-10.97-4.91-10.97-10.97-10.97Z"/>
              <path fill="#0e1423" d="M191.08,130.15c-6.06,0-10.97,4.91-10.97,10.97s4.91,10.97,10.97,10.97,10.97-4.91,10.97-10.97-4.91-10.97-10.97-10.97Z"/>
              <path fill="#0e1423" d="M160.92,141.11h-38.39c-3.03,0-5.54,2.48-5,5.46,2.28,12.53,12.24,21.96,24.19,21.96s21.91-9.43,24.19-21.96c.54-2.98-1.97-5.46-5-5.46Z"/>
            </g>
          </svg>
        </div>
      );
    }
  };

  const profileIcon = renderProfileIcon();
  
  return (
    <div className="branded-welcome-screen">
      <div className="welcome-content">
        {/* Profile Icon - always show immediately for better UX */}
        {profileIcon && (
          <div className="profile-icon-container">
            {profileIcon}
          </div>
        )}
        
        {/* Welcome Text - always show immediately for better UX */}
        <div className="welcome-text">
          <h2>{getTimeBasedGreeting()}</h2>
          {containsMarkdown(welcomeMessage) ? (
            <p dangerouslySetInnerHTML={{ __html: parseWelcomeMarkdown(welcomeMessage) }}></p>
          ) : (
            <p>{welcomeMessage}</p>
          )}
        </div>
        
        {/* CTA Buttons Container - handles both buttons layout */}
        <div className="welcome-buttons-container">
          {/* Main CTA Button - always show for better UX */}
          {true && (
            <button className="welcome-cta-button" onClick={() => {
              // Find the input element
              const inputElement = document.querySelector('.mia-message-input');
              const inputContainer = document.querySelector('.mia-message-input-container');
              const footerElement = document.querySelector('.mia-chat-footer');
              
              if (inputElement && inputContainer && footerElement) {
                // Add visual effects immediately
                inputContainer.classList.add('mia-input-cta-focus');
                footerElement.classList.add('mia-footer-highlight');
                
                // Scroll to footer first, THEN focus to prevent competing scroll behaviors
                footerElement.scrollIntoView({ 
                  behavior: 'smooth', 
                  block: 'end',
                  inline: 'nearest'
                });
                
                // Focus after scroll completes to prevent browser's default focus scroll
                setTimeout(() => {
                  inputElement.focus({ preventScroll: true });
                }, 300);
                
                // Remove visual effects after animations complete
                setTimeout(() => {
                  inputContainer.classList.remove('mia-input-cta-focus');
                }, 2500);
                
                setTimeout(() => {
                  footerElement.classList.remove('mia-footer-highlight');
                }, 2500);
              }
            }}>
              {isHumanControlled && controller && controller.first_name ? (
                <>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2C13.1 2 14 2.9 14 4C14 5.1 13.1 6 12 6C10.9 6 10 5.1 10 4C10 2.9 10.9 2 12 2ZM21 9V7L15 7.5V9M15 11.5C15.8 11.5 16.5 10.8 16.5 10S15.8 8.5 15 8.5 13.5 9.2 13.5 10 14.2 11.5 15 11.5M7 7C8.66 7 10 8.34 10 10C10 11.66 8.66 13 7 13C5.34 13 4 11.66 4 10C4 8.34 5.34 7 7 7Z"/>
                  </svg>
                  {t('askOperator')}
                </>
              ) : isHumanControlled ? (
                <>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                  </svg>
                  {t('askOperator')}
                </>
              ) : (
                <>
                  {t('askMia')}
                  <img 
                    src="https://files.aitomotivelab.com/Mia_Logo_Black.png" 
                    alt="MIA"
                    width="32"
                    height="32"
                    style={{ marginLeft: '8px' }}
                  />
                </>
              )}
            </button>
          )}
          
          {/* WhatsApp Button - show if WhatsApp number is available */}
          {numeroWhatsapp && (
            <button className="whatsapp-button" onClick={handleWhatsAppClick}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488"/>
              </svg>
              <span>{t('whatsappCta')}</span>
            </button>
          )}
        </div>
        
        {/* Footer - always show for better UX */}
        {true && (
          <div className="welcome-footer">
            <div className="powered-by">
              <span>{t('poweredBy')}</span>
              <a href="https://www.aitomotivelab.com/" target="_blank" rel="noopener noreferrer" className="brand-name">
                <span className="brand-ai">ai</span>
                <span className="brand-tomotive">tomotive</span>
                <span className="brand-lab">lab</span>
              </a>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Create a memoized message component to prevent unnecessary re-renders
const MemoizedMessage = React.memo(({ message, isHumanControlled, className }) => {
  return (
    <div className={`mia-message-container ${message.sender === 'user' ? 'user' : 'bot'}`}>
      <MessageBubble 
        message={message} 
        isHumanControlled={isHumanControlled}
        className={className}
      />
    </div>
  );
});

const ChatBody = forwardRef(({ 
  messages: propMessages, 
  isLoading, 
  onSendMessage, 
  onSendOperatorOnly,
  onSendFile,
  onSendAudio,
  onSendPersonalData,
  isHumanControlled,
  humanControlMessage,
  controller,
  mandatoryForm,
  automaticHumanControl,
  showMandatoryForm,
  onFormCompletion,
  isConnected = true,
  isAuthenticated = false,
  statusReceived = false,
  toggleChat,
  toggleChatWidth,
  isChatWide,
  toggleSearchPanel,
  predictedQuestions: propPredictedQuestions,
  onQuestionClick,
  isMobile,
  inputValue,
  setInputValue,
  openedByTrigger,
  setOpenedByTrigger,
  isCompactMode,
  toggleCompactMode,
  ...otherProps 
}, ref) => {
  const language = useMemo(() => getBrowserLanguage(), []);
  const t = useMemo(() => createTranslator(language), [language]);
  const buildContextualWhatsAppMessage = useCallback((currentUrl, miaId) => {
    if (isVehicleDetailPage(currentUrl)) {
      return t('whatsappVdpMessage', { url: currentUrl, miaId });
    }
    return t('whatsappPageMessage', { url: currentUrl, miaId });
  }, [t]);

  // Extract external callbacks from otherProps
  const { onCarSearchStateChange: externalCarSearchCallback, onWelcomeScreenStateChange } = otherProps;
  
  // Use provided messages if available, otherwise use default messages
  const [localMessages, setLocalMessages] = useState([
    { id: 1, text: "Hello! How can I help you today?", sender: "bot" },
    // Example messages for design purposes
  ]);
  
  // Use either prop messages or local messages
  const messages = propMessages || localMessages;
  
  // Create refs for scrolling
  const messagesContainerRef = useRef(null);
  const lastMessageRef = useRef(null);
  const loadingIndicatorRef = useRef(null);
  const isAutoScrollEnabled = useRef(true);
  
  // Reply state for WhatsApp-style reply preview
  const [replyMessage, setReplyMessage] = useState(null);

  // Scroll/touch helpers (must be defined before use in callbacks)
  const scrollTimeoutRef = useRef(null);
  const [isTyping, setIsTyping] = useState(false);
  const typingTimeoutRef = useRef(null);
  const [isFormFocused, setIsFormFocused] = useState(false);
  
  // Single, unified scroll-to-latest function
  const scrollToLatestElement = useCallback((options = {}) => {
    const { 
      behavior = 'smooth', 
      force = false, 
      reason = 'unknown' 
    } = options;
    
    if (!force && !isAutoScrollEnabled.current) {
      return;
    }
    
    if (!force && (isTyping || isFormFocused)) {
      return;
    }
    
    if (!messagesContainerRef.current) return;
    
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }
    
    requestAnimationFrame(() => {
      const container = messagesContainerRef.current;
      if (!container) return;
      
      let targetElement = null;
      
      if (isLoading && loadingIndicatorRef.current) {
        targetElement = loadingIndicatorRef.current;
      } else if (lastMessageRef.current) {
        targetElement = lastMessageRef.current;
      } else {
        const lastContainer = container.querySelector('.mia-aitomotivelab-message-container:last-child');
        const questionsContainer = container.querySelector('.quick-suggestions-container:last-child');
        
        if (reason === 'new bot message arrived' && lastContainer) {
          targetElement = lastContainer.querySelector('.message-bubble') || lastContainer;
        } else if (questionsContainer && !isLoading) {
          targetElement = questionsContainer;
        } else if (lastContainer) {
          targetElement = lastContainer.querySelector('.message-bubble') || lastContainer;
        } else {
          targetElement = container.querySelector('.loading-message');
        }
      }
      
      if (targetElement) {
        let block = 'end';
        let inline = 'nearest';
        
        if (reason === 'AI started typing' && targetElement.classList.contains('loading-message')) {
          block = 'end';
        }
        else if (reason === 'new bot message arrived' || reason === 'human operator message arrived') {
          const isBotMessage = targetElement.closest('.mia-aitomotivelab-message-container.bot') || 
                              targetElement.classList.contains('bot-message');
          if (isBotMessage) {
            block = 'start';
          }
        }
        
        if (isLoading && targetElement.classList.contains('loading-message') && window.innerWidth <= 768) {
          block = 'end';
          targetElement.scrollIntoView({ 
            behavior, 
            block,
            inline
          });
          return;
        }
        
        targetElement.scrollIntoView({ 
          behavior, 
          block,
          inline
        });
      } else {
        const footerOffset = window.innerWidth <= 768 ? 100 : 20;
        window.scrollTo({
          top: document.body.scrollHeight - footerOffset,
          behavior
        });
      }
    });
  }, [isLoading, isTyping, isFormFocused]);

  const handleOpenInlineSearch = useCallback((searchType) => {
    console.log('🔍 ChatBody: Opening inline search:', searchType);
    setCurrentInlineSearchType(searchType);
    setInlineSearchData({
      id: `inline-search-${searchType}-${Date.now()}`,
      type: `inline-search-${searchType}`,
      timestamp: new Date().toISOString()
    });
  }, []);

  const handleCloseInlineSearch = useCallback(() => {
    console.log('🔍 ChatBody: Closing inline search');
    setCurrentInlineSearchType(null);
    setInlineSearchData(null);
    setInlineSearchActions(null);
  }, []);

  useImperativeHandle(ref, () => ({
    scrollToMessage: (index) => {
      const messageElements = messagesContainerRef.current?.querySelectorAll('.message-bubble');
      if (messageElements && messageElements[index]) {
        messageElements[index].scrollIntoView({ behavior: 'smooth', block: 'end' });
      }
    },
    scrollToBottom: (behavior = 'smooth') => {
      scrollToLatestElement({ behavior, force: true, reason: 'external call' });
    },
    focusOnLatestElement: () => {
      scrollToLatestElement({ force: true, reason: 'external focus call' });
    },
    openInlineSearch: handleOpenInlineSearch,
    closeInlineSearch: handleCloseInlineSearch
  }), [scrollToLatestElement]);

  // Effect to handle keyboard appearance on mobile
  useEffect(() => {
    if (!isMobile) return;

    const handleViewportResize = () => {
      if (window.visualViewport) {
        const isKeyboardOpen = window.visualViewport.height < window.innerHeight * 0.8;
        if (isKeyboardOpen) {
          document.body.classList.add('keyboard-open');
          setTimeout(() => {
            scrollToLatestElement({ behavior: 'smooth', force: true, reason: 'keyboard opened' });
          }, 100);
        } else {
          document.body.classList.remove('keyboard-open');
        }
      }
    };

    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', handleViewportResize);
      return () => {
        window.visualViewport.removeEventListener('resize', handleViewportResize);
        document.body.classList.remove('keyboard-open');
      };
    }
  }, [isMobile, scrollToLatestElement]);

  // Listen for reply events from MessageBubble
  useEffect(() => {
    const handleReplyEvent = (event) => {
      const { message } = event.detail;
      setReplyMessage(message);
    };
    
    const handleClearReply = () => {
      setReplyMessage(null);
    };
    
    document.addEventListener('chatReply', handleReplyEvent);
    document.addEventListener('clearReply', handleClearReply);
    
    return () => {
      document.removeEventListener('chatReply', handleReplyEvent);
      document.removeEventListener('clearReply', handleClearReply);
    };
  }, []);
  const lastMessagesLength = useRef(messages.length);
  const lastLoadingState = useRef(isLoading);
  const lastQuestionsVisibility = useRef(false);
  
  // Add state to track if car search panel is open
  const [isCarSearchOpen, setIsCarSearchOpen] = useState(false);
  
  // Add these refs at the top of your component, with the other useRef declarations
  const inputRef = useRef(null);
  const sendButtonRef = useRef(null);
  
  // Add state for handling loading state for the PersonalDataForm
  const [formLoading, setFormLoading] = useState(false);
  
  // Add state to track if questions are visible
  const [areQuestionsVisible, setAreQuestionsVisible] = useState(true);
  
  // Add this at the appropriate position in the component (where other hooks are defined)
  const [toolProgress, setToolProgress] = useState(0);
  
  // Add state to track waiting for operator
  const [isWaitingForOperator, setIsWaitingForOperator] = useState(false);
  
  // Add state for car search panel
  const [showCarSearch, setShowCarSearch] = useState(false);
  
  // Pull-to-refresh state
  const [pullDistance, setPullDistance] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const touchStartRef = useRef(0);
  const PULL_THRESHOLD = 80;

  const handleTouchStart = (e) => {
    if (!isMobile || messagesContainerRef.current.scrollTop > 0) return;
    touchStartRef.current = e.touches[0].clientY;
  };

  const handleTouchMove = (e) => {
    if (!isMobile || isRefreshing || messagesContainerRef.current.scrollTop > 0) return;
    
    const touchY = e.touches[0].clientY;
    const distance = touchY - touchStartRef.current;
    
    if (distance > 0) {
      // Apply resistance to the pull
      const resistedDistance = Math.min(distance * 0.4, PULL_THRESHOLD + 20);
      setPullDistance(resistedDistance);
      
      // Prevent default to stop page scroll
      if (resistedDistance > 5) {
        e.preventDefault();
      }
    }
  };

  const handleTouchEnd = () => {
    if (!isMobile || isRefreshing) return;
    
    if (pullDistance >= PULL_THRESHOLD) {
      triggerRefresh();
    } else {
      setPullDistance(0);
    }
  };

  const triggerRefresh = () => {
    setIsRefreshing(true);
    setPullDistance(PULL_THRESHOLD);
    
    // Simulate refresh or trigger actual history fetch if available
    console.log('🔄 Refreshing chat history...');
    
    // Perform actual refresh by reloading current chat context
    setTimeout(() => {
      // Clear current messages and re-fetch history
      if (typeof window.reconnectChat === 'function') {
        window.reconnectChat();
      } else {
        window.location.reload();
      }
    }, 800);
  };

  // Add these new state variables
  const [showSearchTip, setShowSearchTip] = useState(true);
  const [userHasClickedAvatar, setUserHasClickedAvatar] = useState(false);
  
  // Add these state variables with the other state variables
  const [showPromotionsSearch, setShowPromotionsSearch] = useState(false);
  const [showServiceSearch, setShowServiceSearch] = useState(false);
  const [showRentalSearch, setShowRentalSearch] = useState(false);
  const [showDefaultChat, setShowDefaultChat] = useState(false);
  
  // Add state for inline search processes
  const [currentInlineSearchType, setCurrentInlineSearchType] = useState(null);
  const [inlineSearchData, setInlineSearchData] = useState(null);
  const [inlineSearchActions, setInlineSearchActions] = useState(null);
  
  // Add state to control when to show human control-dependent elements
  const [showHumanControlElements, setShowHumanControlElements] = useState(false);
  const humanControlTimerRef = useRef(null);
  
  // Add state for initial loading timer
  const [initialLoadingComplete, setInitialLoadingComplete] = useState(false);
  const initialLoadingTimerRef = useRef(null);

  // Add state for first message engagement mechanism
  const [isFirstMessageInHumanMode, setIsFirstMessageInHumanMode] = useState(false);
  const [showWelcomeLoading, setShowWelcomeLoading] = useState(false);
  const [welcomeMessage, setWelcomeMessage] = useState(null);
  const welcomeTimerRef = useRef(null);
  
  // Add state for second message engagement mechanism
  const [userMessageCount, setUserMessageCount] = useState(0);
  const [showSecondEngagement, setShowSecondEngagement] = useState(false);
  const [secondEngagementMessage, setSecondEngagementMessage] = useState(null);
  const [showPhoneInputTool, setShowPhoneInputTool] = useState(false);
  const [userConversationHistory, setUserConversationHistory] = useState([]);

  // Calculate if welcome screen is currently active (computed value, not state to avoid infinite renders)
  const isWelcomeScreenCurrentlyActive = useMemo(() => {
    // Check if we should show welcome screen: no messages and not showing mandatory form
    return messages.length === 0 && !showMandatoryForm && !inlineSearchData;
  }, [messages.length, showMandatoryForm, inlineSearchData]);

  // Start initial loading timer when component mounts - REMOVED for better UX
  useEffect(() => {
    // Set initial loading as complete immediately for better user experience
    setInitialLoadingComplete(true);
    
    return () => {
      if (initialLoadingTimerRef.current) {
        clearTimeout(initialLoadingTimerRef.current);
      }
    };
  }, []); // Only run once when component mounts

  // Track when we're in automatic human control mode and should show engagement mechanism
  useEffect(() => {
    // Check if we're in automatic human control mode with no existing messages (fresh chat)
    const shouldTrackFirstMessage = automaticHumanControl === true && 
                                    messages.length === 0 && 
                                    statusReceived &&
                                    initialLoadingComplete;
    
    if (shouldTrackFirstMessage) {
      console.log('👋 ChatBody: Setting up first message engagement tracking for automatic human control', {
        automaticHumanControl,
        messagesLength: messages.length,
        statusReceived,
        initialLoadingComplete,
        dealerName: localStorage.getItem("name_chat") || "not set"
      });
      setIsFirstMessageInHumanMode(true);
      
      // Close search panel if it's open during welcome process
      if (isCarSearchOpen || showCarSearch) {
        console.log('🔒 ChatBody: Closing search panel for welcome process');
        closeAllSearchComponents();
      }
    } else {
      if (isFirstMessageInHumanMode) {
        console.log('👋 ChatBody: Disabling first message engagement tracking', {
          reason: automaticHumanControl !== true ? 'not automatic human control' : 
                  messages.length > 0 ? 'messages exist' : 
                  !statusReceived ? 'status not received' : 
                  !initialLoadingComplete ? 'initial loading not complete' : 'unknown'
        });
      }
      setIsFirstMessageInHumanMode(false);
    }
  }, [automaticHumanControl, messages.length, statusReceived, initialLoadingComplete, isFirstMessageInHumanMode, isCarSearchOpen, showCarSearch]);

  // Cleanup welcome timer on unmount
  useEffect(() => {
    return () => {
      if (welcomeTimerRef.current) {
        clearTimeout(welcomeTimerRef.current);
      }
    };
  }, []);

  // Track user messages and trigger second engagement mechanism
  useEffect(() => {
    // Count user messages and update conversation history
    const userMessages = messages.filter(msg => msg.sender === 'user');
    const newUserCount = userMessages.length;
    
    if (newUserCount !== userMessageCount) {
      console.log('👥 ChatBody: User message count changed from', userMessageCount, 'to', newUserCount);
      setUserMessageCount(newUserCount);
      
      // Update conversation history for WhatsApp
      setUserConversationHistory(userMessages.map(msg => msg.text).filter(Boolean));
      
      // Check if we should trigger second engagement
      // Conditions: user sent 2nd message, still waiting for operator, automatic human control active
      if (newUserCount === 2 && 
          automaticHumanControl === true && 
          isHumanControlled && 
          (!controller || !controller.first_name) && 
          !showSecondEngagement) {
        
        console.log('👥 ChatBody: Triggering second engagement mechanism - user sent 2nd message while waiting');
        
        // Check if dealer has WhatsApp number
        const numeroWhatsapp = localStorage.getItem("numero_whatsapp");
        const hasWhatsApp = numeroWhatsapp && numeroWhatsapp !== "null" && numeroWhatsapp !== "";
        if (hasWhatsApp) {
          // Create WhatsApp engagement message with context-aware URL and message
          const currentUrl = window.location.href;
          const chatId = localStorage.getItem("aitomotivelab_personalData_chat_id");
          const miaId = chatId && chatId !== "undefined" && chatId !== "null" ? ` MIA_ID=${chatId}` : '';
          
          const whatsappMessage = buildContextualWhatsAppMessage(currentUrl, miaId);
          
          const whatsappEngagementMessage = {
            id: `second-engagement-whatsapp-${Date.now()}`,
            text: t('secondEngagementWhatsAppText'),
            sender: 'bot',
            timestamp: new Date().toISOString(),
            type: 'second-engagement-whatsapp',
            showWhatsAppCTA: true,
            whatsappNumber: numeroWhatsapp,
            whatsappMessage: whatsappMessage,
            conversationHistory: userMessages.map(msg => msg.text).filter(Boolean)
          };
          
          setSecondEngagementMessage(whatsappEngagementMessage);
          console.log('📱 ChatBody: Created WhatsApp engagement message');
          
        } else {
          // Create phone input engagement message
          const phoneInputMessage = {
            id: `second-engagement-phone-${Date.now()}`,
            text: t('secondEngagementPhoneText'),
            sender: 'bot',
            timestamp: new Date().toISOString(),
            type: 'second-engagement-phone',
            showPhoneInput: true
          };
          
          setSecondEngagementMessage(phoneInputMessage);
          setShowPhoneInputTool(true);
          console.log('☎️ ChatBody: Created phone input engagement message');
        }
        
        setShowSecondEngagement(true);
      }
    }
  }, [messages, userMessageCount, automaticHumanControl, isHumanControlled, controller, showSecondEngagement, t, buildContextualWhatsAppMessage]);

  const handleInlineSearchSubmit = useCallback((searchType, data) => {
    console.log('🔍 ChatBody: Submitting inline search:', searchType, data);
    
    // Format the search data into a message
    let message = '';
    const contactLine = data.phoneNumber ? `\n- ${t('inlineContactLabel')}: ${data.phoneNumber}` : '';
    switch (searchType) {
      case 'car':
        message = `${t('inlineCarIntro')}
- ${t('inlineCarBudgetLabel')}: ${data.budget}€
- ${t('inlineFuelLabel')}: ${data.fuelType}
${data.additionalInfo ? `- ${t('inlineOtherPrefsLabel')}: ${data.additionalInfo}\n` : ''}${contactLine}

${t('inlineCarQuestion')}`;
        break;
      case 'rental':
        message = `${t('inlineRentalIntro')}
- ${t('inlineUsageLabel')}: ${data.userType}
- ${t('inlineYearlyKmLabel')}: ${data.kmPerYear}
${data.budget ? `- ${t('inlineMonthlyBudgetLabel')}: ${data.budget}${t('perMonth')}\n` : ''}${contactLine}

${t('inlineRentalQuestion')}`;
        break;
      case 'service':
        message = `${t('inlineServiceIntro')}
- ${t('inlineServiceTypeLabel')}: ${data.serviceType}
- ${t('vehicleInfoLabel')}: ${data.vehicleInfo}
${data.preferredDate ? `- ${t('inlinePreferredDateLineLabel')}: ${data.preferredDate}\n` : ''}${contactLine}

${t('inlineServiceQuestion')}`;
        break;
      case 'promotions':
        if (data.promotionType === t('promoAccessories')) {
          message = `${t('inlinePromoAccessoriesIntro')}
- ${t('inlineOfferTypeLabel')}: ${data.promotionType}
- ${t('carModelLabel')}: ${data.carModel}
${contactLine}

${t('inlineAccessoriesQuestion')}`;
        } else if (data.promotionType === t('promoTradeIn')) {
          message = `${t('inlinePromoTradeInIntro')}
- ${t('inlineOfferTypeLabel')}: ${data.promotionType}
- ${t('carModelLabel')}: ${data.carModel}
- ${t('inlineYearLabel')}: ${data.carYear}
- ${t('inlineMileageLineLabel')}: ${data.carKilometers} km
${contactLine}

${t('inlineTradeInQuestion')}`;
        } else {
          message = `${t('inlinePromoGeneralIntro')}
- ${t('inlineOfferTypeLabel')}: ${data.promotionType}
${data.budget ? `- ${t('inlineBudgetInterestLineLabel')}: ${data.budget}€\n` : ''}${data.interests ? `- ${t('inlineOtherDetailsLineLabel')}: ${data.interests}\n` : ''}${contactLine}

${t('inlinePromoQuestion')}`;
        }
        break;
      default:
        message = t('genericInfoMessage');
    }

    // Close the search interface
    handleCloseInlineSearch();
    
    // Send the formatted message
    if (onSendMessage) {
      onSendMessage(message);
    }
  }, [handleCloseInlineSearch, onSendMessage, t]);

  // Hide second engagement tools when operator connects or AI takes over
  useEffect(() => {
    // Check if operator has connected (has controller with name) or AI has taken over
    const operatorConnected = isHumanControlled && controller && controller.first_name;
    const aiTookOver = !isHumanControlled && !automaticHumanControl;
    
    if ((operatorConnected || aiTookOver) && (showPhoneInputTool || showSecondEngagement)) {
      console.log('🔄 ChatBody: Hiding second engagement tools - operator connected or AI took over', {
        operatorConnected,
        aiTookOver,
        operatorName: controller?.first_name || 'none'
      });
      
      setShowPhoneInputTool(false);
      setShowSecondEngagement(false);
      setSecondEngagementMessage(null);
    }
  }, [isHumanControlled, controller, automaticHumanControl, showPhoneInputTool, showSecondEngagement]);

  // Listen for custom event to open inline search from DefaultChat
  useEffect(() => {
    const handleInlineSearchEvent = (event) => {
      const { searchType } = event.detail;
      console.log('🔍 ChatBody: Received openInlineSearch event:', searchType);
      handleOpenInlineSearch(searchType);
    };

    window.addEventListener('openInlineSearch', handleInlineSearchEvent);
    
    return () => {
      window.removeEventListener('openInlineSearch', handleInlineSearchEvent);
    };
  }, [handleOpenInlineSearch]);

  // Debug effect to monitor search panel state changes
  useEffect(() => {
    if (isCarSearchOpen) {
      console.warn('⚠️ ChatBody: Search panel is OPEN - Debug info:', {
        isCarSearchOpen,
        showCarSearch,
        welcomeMessage: !!welcomeMessage,
        showWelcomeLoading,
        isFirstMessageInHumanMode,
        automaticHumanControl,
        messagesCount: messages.length,
        stackTrace: new Error().stack
      });
      
      // If search panel opened during welcome process, force close it
      if (welcomeMessage || showWelcomeLoading || isFirstMessageInHumanMode || automaticHumanControl) {
        // Force search panel to close during welcome process
        setTimeout(() => {
          setIsCarSearchOpen(false);
          setShowCarSearch(false);
        }, 0);
      }
    }
  }, [isCarSearchOpen, showCarSearch, welcomeMessage, showWelcomeLoading, isFirstMessageInHumanMode, automaticHumanControl, messages.length]);

  // Main scroll management effect
  useEffect(() => {
    // Skip all scroll management when welcome screen is showing
    if (isWelcomeScreenCurrentlyActive) {
      return; // Don't perform any scrolling when welcome screen is active
    }
    
    const messagesChanged = messages.length !== lastMessagesLength.current;
    const loadingChanged = isLoading !== lastLoadingState.current;
    const welcomeLoadingChanged = showWelcomeLoading;
    
    let shouldScroll = false;
    let scrollReason = '';
    let forceScroll = false;
    
    // Case 1: User sent a message (new user message) - ALWAYS scroll regardless of position
    if (messagesChanged && messages.length > 0) {
      const latestMessage = messages[messages.length - 1];
      if (latestMessage.sender === 'user') {
        shouldScroll = true;
        scrollReason = isHumanControlled ? 'user sent message (human control active)' : 'user sent message';
        forceScroll = true; // Always force scroll when user sends message
      }
    }
    
    // Case 2: Bot/AI typing started - ALWAYS scroll to show typing indicator
    if (loadingChanged && isLoading) {
      shouldScroll = true;
      scrollReason = 'AI started typing';
      forceScroll = true; // Force scroll to show typing indicator
    }
    
    // Case 2.5: Welcome loading started - ALWAYS scroll to show welcome loading indicator
    if (welcomeLoadingChanged) {
      shouldScroll = true;
      scrollReason = 'Welcome loading started';
      forceScroll = true; // Force scroll to show welcome loading indicator
    }
    
    // Case 3: New bot message arrived (loading stopped) - ALWAYS scroll to show the new message
    if (loadingChanged && !isLoading) {
      shouldScroll = true;
      scrollReason = 'new bot message arrived';
      forceScroll = true; // Always scroll to show the completed message, even if user scrolled away during typing
    }
    
    // Case 4: Human operator message arrived - ALWAYS scroll to show the new message
    if (messagesChanged && messages.length > 0 && !shouldScroll) {
      const latestMessage = messages[messages.length - 1];
      // Check if it's a bot/server message that arrived while human control is active
      // or if it's from a human operator (admin_message type)
      // Also check if it's a recent message (not from history)
      const isRecentMessage = !latestMessage.loading && latestMessage.text && latestMessage.text.trim() !== '';
      const isHumanOperatorMessage = (latestMessage.sender === 'bot' || latestMessage.sender === 'server') && 
                                     (isHumanControlled || latestMessage.type === 'admin_message');
      
      if (isRecentMessage && isHumanOperatorMessage) {
        shouldScroll = true;
        scrollReason = 'human operator message arrived';
        forceScroll = true; // Always force scroll for human operator messages
      }
    }
    
    // Update tracking refs AFTER we've determined what to do
    lastMessagesLength.current = messages.length;
    lastLoadingState.current = isLoading;
    
    if (shouldScroll) {
      // Use different delays based on the scroll reason for optimal UX
      let delay = 50; // Default delay
      
      if (scrollReason === 'AI started typing' || scrollReason === 'Welcome loading started') {
        delay = 100; // Slightly longer delay for tool loading to ensure DOM is ready
      } else if (scrollReason === 'new bot message arrived') {
        delay = 150; // Longer delay for completed messages to allow content to render
      } else if (forceScroll) {
        delay = 50; // Quick delay for user actions
      }
      
      setTimeout(() => {
        scrollToLatestElement({ 
          reason: scrollReason,
          force: forceScroll
        });
      }, delay);
    }
  }, [messages, isLoading, isHumanControlled, scrollToLatestElement, showWelcomeLoading, isWelcomeScreenCurrentlyActive]);

  // Handle scrolling when welcome message appears
  useEffect(() => {
    // Skip welcome message scrolling if branded welcome screen is active
    if (isWelcomeScreenCurrentlyActive) {
      return; // Don't scroll when branded welcome screen is showing
    }
    
    if (welcomeMessage && !showWelcomeLoading) {
      console.log('👋 ChatBody: Welcome message appeared, scrolling to show it');
      setTimeout(() => {
        scrollToLatestElement({ 
          reason: 'welcome message appeared',
          force: true
        });
      }, 100);
    }
  }, [welcomeMessage, showWelcomeLoading, scrollToLatestElement, isWelcomeScreenCurrentlyActive]);

  // Keep the scroll event listener for manual scroll detection
  useEffect(() => {
    // Skip scroll listener when welcome screen is showing
    if (isWelcomeScreenCurrentlyActive) {
      return; // Don't attach scroll listener when welcome screen is active
    }
    
    const messagesContainer = messagesContainerRef.current;
    if (messagesContainer) {
      const handleScroll = () => {
        const { scrollTop, scrollHeight, clientHeight } = messagesContainer;
        // More forgiving threshold - only disable if user scrolls significantly away (200px)
        const isNearBottom = scrollHeight - scrollTop - clientHeight < 200;
        const wasEnabled = isAutoScrollEnabled.current;
        isAutoScrollEnabled.current = isNearBottom;
        
        // Debug logging
        if (wasEnabled !== isNearBottom) {
          // Auto-scroll enabled changed
        }
      };
      
      messagesContainer.addEventListener('scroll', handleScroll);
      
      // Initialize auto-scroll as enabled
      isAutoScrollEnabled.current = true;
      
      return () => messagesContainer.removeEventListener('scroll', handleScroll);
    }
  }, [isWelcomeScreenCurrentlyActive]);

  // Process predicted questions to ensure they're in the right format
  const processedPredictedQuestions = useMemo(() => {
    
    if (!propPredictedQuestions) return [];
    
    // If it's already an array of strings or objects with text property, use it directly
    if (Array.isArray(propPredictedQuestions)) {
      if (propPredictedQuestions.length === 0) return [];
      
      // Check if array contains objects with text property or strings
      if (typeof propPredictedQuestions[0] === 'string') {
        return propPredictedQuestions;
      } else if (typeof propPredictedQuestions[0] === 'object' && propPredictedQuestions[0].text) {
        return propPredictedQuestions;
      }
    }
    
    // If it's an object with next_question_X properties
    if (typeof propPredictedQuestions === 'object' && !Array.isArray(propPredictedQuestions)) {
      const extractedQuestions = [];
      
      // Extract questions from next_question_X properties
      if (propPredictedQuestions.next_question_1) 
        extractedQuestions.push(propPredictedQuestions.next_question_1);
      if (propPredictedQuestions.next_question_2) 
        extractedQuestions.push(propPredictedQuestions.next_question_2);
      if (propPredictedQuestions.next_question_3) 
        extractedQuestions.push(propPredictedQuestions.next_question_3);
      
      if (extractedQuestions.length > 0) {
        return extractedQuestions;
      }
      
      // If it's a different object format, extract values
      const objQuestions = Object.values(propPredictedQuestions)
        .filter(q => q && (typeof q === 'string' || (typeof q === 'object' && q.text)));
      
      if (objQuestions.length > 0) {
        return objQuestions;
      }
    }
    
    return [];
  }, [propPredictedQuestions]);
  
  // Track when predicted questions change
  const [lastProcessedQuestions, setLastProcessedQuestions] = useState([]);
  
  // Update lastProcessedQuestions when processedPredictedQuestions changes
  useEffect(() => {
    if (processedPredictedQuestions.length > 0) {
      // Convert questions to comparable format
      const getQuestionText = q => typeof q === 'string' ? q : q.text;
      const currentQuestions = processedPredictedQuestions.map(getQuestionText);
      const lastQuestions = lastProcessedQuestions.map(getQuestionText);
      
      // Check if questions have actually changed
      const currentStr = JSON.stringify(currentQuestions.sort());
      const lastStr = JSON.stringify(lastQuestions.sort());
      
      if (currentStr !== lastStr) {
        setLastProcessedQuestions(processedPredictedQuestions);
      }
    }
  }, [processedPredictedQuestions]);
  
  // Add handler for user interactions to exit trigger mode
  const handleUserInteraction = () => {
    if (openedByTrigger && setOpenedByTrigger) {
      setOpenedByTrigger(false);
    }
  };

  // Simplified send message handler
  const handleSendMessage = (text, replyInfo = null) => {
    console.log('📤 Sending message:', text, 'with reply:', replyInfo);
    
    // Use replyMessage if replyInfo not provided (for backward compatibility)
    const finalReplyInfo = replyInfo || (replyMessage ? {
      id: replyMessage.id,
      text: replyMessage.text,
      sender: replyMessage.sender
    } : null);
    
    // Clear reply state after sending
    if (replyMessage) {
      setReplyMessage(null);
    }
    
    // Clear openedByTrigger state when user sends a message
    if (openedByTrigger && setOpenedByTrigger) {
      setOpenedByTrigger(false);
    }
    
    // Check if this is the first message during automatic human control mode
    if (isFirstMessageInHumanMode && !showWelcomeLoading && !welcomeMessage) {
      console.log('👋 ChatBody: First message in automatic human control - triggering engagement mechanism');
      
      // Get dealer name from localStorage
      const dealerName = localStorage.getItem("name_chat") || "questa concessionaria";
      
      // Create the welcome message
      const welcomeMessageText = t('welcomeConnectedMessage', { dealerName });
      
      // Show typing indicator immediately
      setShowWelcomeLoading(true);
      
      // Calculate timestamp for welcome message - should be just after the user's message
      // Use current time + a minimal delay to ensure proper ordering
      const welcomeTimestamp = new Date(Date.now() + 50).toISOString();
      
      // Set timer for 5 seconds to show the welcome message
      welcomeTimerRef.current = setTimeout(() => {
        setShowWelcomeLoading(false);
        setWelcomeMessage({
          id: `welcome-first-${Date.now()}`,
          text: welcomeMessageText,
          sender: 'bot',
          timestamp: welcomeTimestamp, // Use pre-calculated timestamp
          type: 'human-operator-welcome'
        });
        
        // Reset the first message tracking
        setIsFirstMessageInHumanMode(false);
        
        console.log('👋 ChatBody: Welcome message displayed after 5 second delay');
      }, 5000); // 5 seconds delay
      
      console.log('👋 ChatBody: Started welcome loading indicator');
    }
    
    // Send the original message with reply info
    if (onSendMessage) {
      onSendMessage(text, replyMessage ? {
        id: replyMessage.id,
        text: replyMessage.text,
        sender: replyMessage.sender
      } : null);
    }
  };

  // Simplified form completion handler for now
  const handleOfflineFormCompletion = (personalData) => {
    console.log('📋 ChatBody: Offline form completed', personalData);
    
    // Reset first message tracking if active
    if (isFirstMessageInHumanMode) {
      setIsFirstMessageInHumanMode(false);
      console.log('👋 ChatBody: Reset first message tracking due to form completion');
    }
    
    if (onFormCompletion) {
      onFormCompletion(personalData);
    }
  };

  // Handle question click
  const handleQuestionClick = (question) => {
    const questionText = typeof question === 'string' ? question : question.text;
    
    // Just set the input value without sending
    setInputValue(questionText);
  };

  // Handle questions visibility change
  const handleQuestionsVisibilityChange = useCallback((isVisible) => {
    // Update questions visibility state
    setAreQuestionsVisible(isVisible);
    
    // Check if visibility actually changed
    if (isVisible !== lastQuestionsVisibility.current) {
      lastQuestionsVisibility.current = isVisible;
      
      // Force scroll adjustment when questions panel visibility changes
      setTimeout(() => {
        if (isAutoScrollEnabled.current) {
          scrollToLatestElement({ 
            reason: 'questions visibility change',
            force: true
          });
        }
      }, 300); // Give time for the panel animation to start
    }
  }, [scrollToLatestElement]);

  // Add Material Icons font to the document head
  useEffect(() => {
    // Check if the Material Icons font is already loaded
    const linkExists = document.querySelector('link[href*="material-icons"]');
    if (!linkExists) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = 'https://fonts.googleapis.com/icon?family=Material+Icons';
      document.head.appendChild(link);
    }
  }, []);

  // Add event listeners to detect when form fields gain or lose focus
  useEffect(() => {
    const handleFormFocus = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'SELECT' || e.target.tagName === 'TEXTAREA') {
        const isFormField = e.target.closest('.personal-data-form') !== null;
        if (isFormField) {
          setIsFormFocused(true);
        }
      }
    };

    const handleFormBlur = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'SELECT' || e.target.tagName === 'TEXTAREA') {
        const isFormField = e.target.closest('.personal-data-form') !== null;
        if (isFormField) {
          // Add small delay to allow for clicking other form elements
          setTimeout(() => {
            const activeElement = document.activeElement;
            const stillInForm = activeElement && activeElement.closest('.personal-data-form') !== null;
            if (!stillInForm) {
              setIsFormFocused(false);
            }
          }, 10);
        }
      }
    };

    document.addEventListener('focus', handleFormFocus, true);
    document.addEventListener('blur', handleFormBlur, true);

    return () => {
      document.removeEventListener('focus', handleFormFocus, true);
      document.removeEventListener('blur', handleFormBlur, true);
    };
  }, []);

  // Remove old greeting logic - now handled by BrandedWelcomeScreen

  // Add this useEffect to check if the user has used the search feature before
  useEffect(() => {
    const hasUsedSearch = localStorage.getItem('hasUsedCarSearch') === 'true';
    setUserHasClickedAvatar(hasUsedSearch);
    
    // If they've never used it, show the tip and set a timer to remove it
    if (!hasUsedSearch) {
      setShowSearchTip(true);
      const timer = setTimeout(() => {
        setShowSearchTip(false);
      }, 10000); // Show tip for 10 seconds
      return () => clearTimeout(timer);
    } else {
      setShowSearchTip(false);
    }
  }, []);

  // Function to close all search components
  const closeAllSearchComponents = () => {
    setShowCarSearch(false);
    setIsCarSearchOpen(false);
  };

  // Handle car search close
  const handleCarSearchClose = () => {
    closeAllSearchComponents();
    handleCarSearchStateChange(false);
  };

  // Open car search
  const handleCarSearchOpen = () => {
    // Prevent opening during welcome message process or automatic human control
    if (welcomeMessage || showWelcomeLoading || isFirstMessageInHumanMode || automaticHumanControl) {
      console.warn('🚫 ChatBody: Preventing car search from opening during welcome/automatic human control process');
      return;
    }
    
    console.log('🔍 ChatBody: Opening car search');
    // Close any other open search components first
    closeAllSearchComponents();
    
    // Now open the car search
    setShowCarSearch(true);
    handleCarSearchStateChange(true);
  };

  // Helper function to generate a more natural fuel type preference statement
  const getFuelTypePreference = useCallback((fuelType) => {
    switch (fuelType) {
      case t('gasoline'):
      case 'Benzina':
        return t('fuelPrefGasoline');
      case t('diesel'):
      case 'Diesel':
        return t('fuelPrefDiesel');
      case t('hybrid'):
      case 'Ibrida':
        return t('fuelPrefHybrid');
      case t('electric'):
      case 'Elettrica':
        return t('fuelPrefElectric');
      case t('noPreference'):
      case 'Non ho preferenze':
        return t('fuelPrefNoPreference');
      default:
        return "";
    }
  }, [t]);

  // Handle car search submission
  const handleCarSearchSubmit = useCallback((searchData) => {
    setShowCarSearch(false);
    setIsCarSearchOpen(false);
    
    // Format the search data into a message that triggers the search tool
    const message = `${t('carSearchToolIntro')}
${t('carSearchToolBudgetLine', { budget: searchData.budget })}
${getFuelTypePreference(searchData.fuelType)}
${searchData.additionalInfo ? `\n${t('carSearchToolAdditionalPrefsLine', { additionalInfo: searchData.additionalInfo })}` : ''}
${searchData.phoneNumber ? `\n${t('inlineContactLabel')}: ${searchData.phoneNumber}` : ''}

${t('carSearchToolQuestion')}`;

    if (onSendMessage) {
      onSendMessage(message);
    }
  }, [getFuelTypePreference, onSendMessage, t]);

  // Memoize the messages to prevent unnecessary re-renders
  const memoizedMessages = useMemo(() => {
    let messagesToProcess = messages;

    console.log('🔄 ChatBody: Processing messages:', {
      messageCount: messages.length,
      showMandatoryForm,
      mandatoryForm,
      automaticHumanControl
    });

    // SIMPLIFIED: Basic mandatory form logic - we'll rebuild the offline workflow properly
    if (showMandatoryForm) {
      console.log('📋 Showing basic mandatory form message');
      messagesToProcess = [
        { 
          id: 'mandatory-form', 
          text: t('completeForm'),
          sender: 'bot',
          type: 'system',
          timestamp: new Date().toISOString(),
          categories: [],
          form: 'mandatory_personal_data',
          showForm: true
        }
      ];
    } else {
      // Standard message filtering (keep the existing logic for normal messages)
      const filteredMessages = messages.filter(message => {
        // Always keep user messages
        if (message.sender === 'user') {
          return true;
        }
        
        // Keep system/welcome messages
        if (message.id === 'welcome' || message.type === 'system') {
          return true;
        }
        
        // For bot/server messages, only show if they have actual content OR carousels
        const hasContent = message.text && message.text.trim() !== '';
        const hasCarousel = message.carouselItems && message.carouselItems.length > 0;
        const isLoadingMessage = message.loading === true;
        
        // During human operator mode, be more strict - only show messages with content
        if (isHumanControlled) {
          return hasContent && !isLoadingMessage;
        }
        
        // For AI mode: Show completed messages and loading messages with content
        if (isLoadingMessage) {
          // Show loading messages that have content (streaming messages)
          return hasContent;
        }
        
        return hasContent || hasCarousel;
      });

      // Remove duplicate messages
      const uniqueMessages = filteredMessages.reduce((acc, message, index) => {
        const prevMessage = acc[acc.length - 1];
        if (prevMessage && 
            prevMessage.text === message.text && 
            prevMessage.sender === message.sender &&
            prevMessage.id !== 'welcome' && 
            message.id !== 'welcome') {
          return acc;
        }
        
        if (!message.text || message.text.trim() === '') {
          const hasRecentValidMessage = acc.some(msg => 
            msg.sender === message.sender && 
            msg.text && 
            msg.text.trim() !== '' &&
            Math.abs((new Date(msg.timestamp || 0)) - (new Date(message.timestamp || 0))) < 5000
          );
          if (hasRecentValidMessage) {
            return acc;
          }
          
          if ((message.sender === 'bot' || message.sender === 'server') && !message.loading) {
            return acc;
          }
        }
        
        acc.push(message);
        return acc;
      }, []);
      
      // Skip initial loading screen for better UX - show welcome screen immediately
      const isWaitingForConnection = !statusReceived || (!isConnected && !isAuthenticated);
      
      // Only show loading if we're specifically waiting for connection AND have no messages to show
      // This eliminates the mandatory loading screen for better user experience
      const shouldShowInitialLoading = false; // Disabled for improved UX
      
      if (shouldShowInitialLoading && !showMandatoryForm) {
        // Notify parent that welcome screen is not showing (loading screen is)
        if (onWelcomeScreenStateChange) {
          onWelcomeScreenStateChange(false);
        }
        
        // This block is now disabled to remove loading skeleton
        messagesToProcess = [{ 
          id: 'connection-loading', 
          type: 'connection-loading',
          timestamp: new Date().toISOString(),
          categories: []
        }];
      }
      // Show inline search screen if active
      else if (inlineSearchData) {
        // Notify parent that welcome screen is not showing (inline search is)
        if (onWelcomeScreenStateChange) {
          onWelcomeScreenStateChange(false);
        }
        
        console.log('🔍 ChatBody: Showing inline search screen:', currentInlineSearchType);
        messagesToProcess = [inlineSearchData];
      }
      // Show branded welcome screen immediately if no messages and not showing mandatory form
      // Improved UX: show welcome screen right away without waiting for connection status
      else if (uniqueMessages.length === 0 && !showMandatoryForm) {
        // Notify parent that welcome screen is showing
        if (onWelcomeScreenStateChange) {
          onWelcomeScreenStateChange(true);
        }

        messagesToProcess = [{ 
          id: 'branded-welcome', 
          type: 'branded-welcome',
          timestamp: new Date().toISOString(),
          categories: []
        }];
      } else {
        // Notify parent that welcome screen is not showing
        if (onWelcomeScreenStateChange) {
          onWelcomeScreenStateChange(false);
        }
        
        console.log('🗨️ ChatBody: Showing existing chat messages:', uniqueMessages.length);
        messagesToProcess = uniqueMessages;
      }
    }
    
    // Add welcome message engagement mechanism for automatic human control
    if (welcomeMessage) {
      console.log('👋 ChatBody: Adding welcome message to displayed messages');
      
      // Find the first user message and insert welcome message right after it
      let insertIndex = messagesToProcess.length; // Default to end
      
      for (let i = 0; i < messagesToProcess.length; i++) {
        if (messagesToProcess[i].sender === 'user') {
          // Insert welcome message right after the first user message
          insertIndex = i + 1;
          break;
        }
      }
      
      messagesToProcess.splice(insertIndex, 0, welcomeMessage);
      console.log('👋 ChatBody: Welcome message inserted at index', insertIndex, 'after first user message. Total messages:', messagesToProcess.length);
      
      // Debug: Log the message order after insertion
      console.log('👋 ChatBody: Message order after welcome insertion:', 
        messagesToProcess.map((msg, idx) => ({
          index: idx,
          sender: msg.sender,
          type: msg.type,
          text: (msg.text || '').substring(0, 50) + '...',
          timestamp: msg.timestamp
        }))
      );
    }
    
    // Add second engagement message for enhanced user retention
    if (secondEngagementMessage) {
      console.log('👥 ChatBody: Adding second engagement message to displayed messages');
      
      // Find the second user message and insert second engagement message right after it
      let insertIndex = messagesToProcess.length; // Default to end
      let userMessageCount = 0;
      
      for (let i = 0; i < messagesToProcess.length; i++) {
        if (messagesToProcess[i].sender === 'user') {
          userMessageCount++;
          if (userMessageCount === 2) {
            // Insert second engagement message right after the second user message
            insertIndex = i + 1;
            break;
          }
        }
      }
      
      messagesToProcess.splice(insertIndex, 0, secondEngagementMessage);
      console.log('👥 ChatBody: Second engagement message inserted at index', insertIndex, 'after second user message. Total messages:', messagesToProcess.length);
    }
    
    // Map messages to rendered components
    return messagesToProcess.map((message, index) => {
      const hasCarousel = message.carouselItems && message.carouselItems.length > 0;
      const isLastMessage = index === messagesToProcess.length - 1;
      const isBot = message.sender === 'bot' || message.sender === 'server' || message.type === 'system';
      
      // Check if this is the connection loading screen
      if (message.type === 'connection-loading') {
        return (
          <div 
            key={message.id || `msg-${index}`}
            className="mia-message-container connection-loading"
            ref={isLastMessage ? lastMessageRef : null}
          >
            <div className="connection-loading-screen">
              <div className="connection-loading-content">
                <div className="profile-icon-container">
                  <div className="profile-icon loading-icon">
                    <div className="connection-spinner">
                      <div className="spinner-ring"></div>
                      <div className="spinner-ring"></div>
                      <div className="spinner-ring"></div>
                    </div>
                  </div>
                </div>
                <div className="connection-loading-text">
                  <h2>{t('refreshing')}</h2>
                  <p>{t('writeMessage')}</p>
                </div>
              </div>
            </div>
          </div>
        );
      }
      
      // Check if this is the branded welcome screen
      if (message.type === 'branded-welcome') {
        return (
          <div 
            key={message.id || `msg-${index}`}
            className="mia-message-container branded-welcome"
            ref={isLastMessage ? lastMessageRef : null}
          >
            <BrandedWelcomeScreen 
              onSendMessage={handleSendMessage}
              isMobile={isMobile}
              isHumanControlled={isHumanControlled}
              controller={controller}
              showHumanControlElements={showHumanControlElements}
              automaticHumanControl={automaticHumanControl}
              isConnected={isConnected}
              isAuthenticated={isAuthenticated}
              statusReceived={statusReceived}
              t={t}
            />
          </div>
        );
      }
      
      // Check if this is an inline search screen
      if (message.type?.startsWith('inline-search-')) {
        const searchType = message.type.replace('inline-search-', '');
        
        const commonProps = {
          onSendMessage: handleSendMessage,
          onClose: handleCloseInlineSearch,
          onSubmit: (data) => handleInlineSearchSubmit(searchType, data),
          isMobile,
          onActionsChange: setInlineSearchActions,
          t
        };
        
        let SearchComponent;
        switch (searchType) {
          case 'car':
            SearchComponent = InlineCarSearchScreen;
            break;
          case 'rental':
            SearchComponent = InlineRentalSearchScreen;
            break;
          case 'service':
            SearchComponent = InlineServiceSearchScreen;
            break;
          case 'promotions':
            SearchComponent = InlinePromotionsSearchScreen;
            break;
          case 'default':
            // For default chat, we'll use a different rendering approach
            return (
              <div 
                key={message.id || `msg-${index}`}
                className={`mia-message-container default-chat-search`}
                ref={isLastMessage ? lastMessageRef : null}
              >
                <DefaultChatScreen 
                  onSendMessage={handleSendMessage}
                  onClose={handleCloseInlineSearch}
                  onSubmit={(data) => {
                    handleCloseInlineSearch();
                    if (onSendMessage && data.message) {
                      onSendMessage(data.message);
                    }
                  }}
                  isMobile={isMobile}
                  t={t}
                />
              </div>
            );
          default:
            return null;
        }
        
        return (
          <div 
            key={message.id || `msg-${index}`}
            className={`mia-message-container inline-search ${searchType}-search`}
            ref={isLastMessage ? lastMessageRef : null}
          >
            <SearchComponent {...commonProps} />
          </div>
        );
      }
      
      // Check if we should show the personal data form
      const shouldShowPersonalDataForm = (message.sender === 'operator' && message.form) ||
                                          (message.showForm && message.form) ||
                                          (message.id === 'mandatory-form' && showMandatoryForm);
      
      return (
        <div 
          key={message.id || message._id || `msg-${index}`}
          className={`mia-message-container ${isBot ? 'bot' : 'user'}${isLastMessage ? ' last-message' : ''}`}
          ref={isLastMessage ? lastMessageRef : null}
          data-message-id={message.id || message._id}
        >
          {isBot ? (
            <>
              <MessageBubble 
                message={{...message, sender: 'bot'}} 
                isHumanControlled={isHumanControlled}
                className={hasCarousel ? 'has-carousel' : ''}
                onSendMessage={handleSendMessage}
              />
              {shouldShowPersonalDataForm && (
                <div className={`mia-personal-data-form-container ${isCompactMode ? 'compact-mode' : ''}`}>
                  <PersonalDataForm
                    onSubmit={onSendPersonalData}
                    onSendMessage={handleSendMessage}
                    setIsLoading={setFormLoading}
                    activeTrigger={message.form}
                    isCompactMode={isCompactMode}
                    onFormCompletion={handleOfflineFormCompletion}
                    isMandatory={message.id === 'mandatory-form'}
                  />
                </div>
              )}
            </>
          ) : (
            <MessageBubble 
              message={message} 
              isHumanControlled={isHumanControlled}
              className={hasCarousel ? 'has-carousel' : ''}
              onSendMessage={handleSendMessage}
            />
          )}
        </div>
      );
    });
  }, [messages, isLoading, isHumanControlled, lastMessageRef, onSendPersonalData, handleSendMessage, setFormLoading, isCompactMode, handleOfflineFormCompletion, showMandatoryForm, mandatoryForm, automaticHumanControl, isMobile, initialLoadingComplete, statusReceived, isConnected, isAuthenticated, showHumanControlElements, welcomeMessage, secondEngagementMessage, inlineSearchData, currentInlineSearchType, handleCloseInlineSearch, handleInlineSearchSubmit, t]);
  
  // Render quick suggestions
  const renderQuickSuggestions = () => {
    // Only render if we have questions and not loading and not human controlled
    if (
      Array.isArray(processedPredictedQuestions) && 
      processedPredictedQuestions.length > 0 && 
      !isLoading && 
      !isHumanControlled
    ) {
      // Check if the last message has a carousel
      const lastMessage = messages.length > 0 ? messages[messages.length - 1] : null;
      const hasCarousel = lastMessage && lastMessage.carouselItems && lastMessage.carouselItems.length > 0;
      
      return (
        <div className={`mia-quick-suggestions-container ${hasCarousel ? 'after-carousel' : ''}`}>
          <div className="mia-quick-suggestions">
            {processedPredictedQuestions.map((question, index) => {
              const questionText = typeof question === 'string' ? question : question.text;
              return (
                <button
                  key={index}
                  className="mia-suggestion-chip"
                  onClick={() => handleQuestionClick(question)}
                >
                  {questionText}
                </button>
              );
            })}
          </div>
        </div>
      );
    }
    return null;
  };

  // Enhanced tool progress with better timing and visual feedback
  useEffect(() => {
    let progressInterval = null;
    
    // Reset and start tool progress when loading begins with a tool
    if (isLoading && messages.length > 0 && messages[messages.length - 1]?.tool) {
      setToolProgress(0);
      
      // Get the tool name for context
      const lastMessage = messages[messages.length - 1];
      const toolName = Array.isArray(lastMessage?.tool) 
        ? lastMessage.tool[0] 
        : lastMessage?.tool;
      
      // More realistic progress speeds based on tool type
      const getProgressConfig = (tool) => {
        // Ensure tool is a string before calling includes
        const toolStr = tool || '';
        if (typeof toolStr !== 'string') {
          return { speed: 1.0, pauseAt: [35, 65, 80] };
        }
        
        if (toolStr.includes('car_stock_search')) return { speed: 1.2, pauseAt: [30, 60, 85] };
        if (toolStr.includes('booking')) return { speed: 0.8, pauseAt: [25, 50, 75] };
        if (toolStr.includes('knowledge')) return { speed: 1.5, pauseAt: [40, 70] };
        if (toolStr.includes('consultant_intervention')) return { speed: 2.0, pauseAt: [90] };
        return { speed: 1.0, pauseAt: [35, 65, 80] };
      };
      
      const config = getProgressConfig(toolName);
      let currentPauseIndex = 0;
      let isPaused = false;
      let pauseTimeRemaining = 0;
      
      progressInterval = setInterval(() => {
        if (isPaused) {
          pauseTimeRemaining--;
          if (pauseTimeRemaining <= 0) {
            isPaused = false;
          }
          return;
        }
        
        setToolProgress(prev => {
          const newProgress = prev + config.speed;
          
          // Check if we should pause at certain points
          if (currentPauseIndex < config.pauseAt.length) {
            const pausePoint = config.pauseAt[currentPauseIndex];
            if (newProgress >= pausePoint && prev < pausePoint) {
              isPaused = true;
              pauseTimeRemaining = Math.random() * 15 + 10; // 10-25 intervals (0.6-1.5 seconds)
              currentPauseIndex++;
            }
          }
          
          // Never go above 95% until actual completion
          const maxProgress = 95;
          return newProgress >= maxProgress ? maxProgress : newProgress;
        });
      }, 40); // Faster update for smoother animation
    } else {
      setToolProgress(0);
    }
    
    return () => {
      if (progressInterval) clearInterval(progressInterval);
    };
  }, [isLoading, messages]);

  // Add callback function to track car search state
  const handleCarSearchStateChange = (isOpen) => {
    // Prevent search panel from opening during welcome message process or automatic human control
    if (isOpen && (welcomeMessage || showWelcomeLoading || isFirstMessageInHumanMode || automaticHumanControl)) {
      console.warn('🚫 ChatBody: Preventing search panel from opening during welcome/automatic human control process', {
        welcomeMessage: !!welcomeMessage,
        showWelcomeLoading,
        isFirstMessageInHumanMode,
        automaticHumanControl,
        currentMessages: messages.length
      });
      return; // Don't open the search panel
    }
    
    console.log('🔍 ChatBody: Car search state changing to:', isOpen);
    setIsCarSearchOpen(isOpen);
    
    // Call external callback if available
    if (externalCarSearchCallback) {
      externalCarSearchCallback(isOpen);
    }
    
    if (isOpen) {
      // When car search opens, ensure the chat container doesn't interfere
      // Only block scrolling if not in compact mode
      if (!isCompactMode && !openedByTrigger) {
        document.body.style.overflow = 'hidden';
      }
      const chatContainer = document.querySelector('.chat-container');
      if (chatContainer) {
        chatContainer.classList.add('car-search-active');
      }
    } else {
      // When car search closes, restore normal state based on compact mode
      if (isCompactMode || openedByTrigger) {
        // Keep scroll enabled in compact mode
        document.body.style.overflow = 'auto';
      } else {
        // In full screen mode, respect the main chat container's overflow setting
        document.body.style.overflow = '';
      }
      const chatContainer = document.querySelector('.chat-container');
      if (chatContainer) {
        chatContainer.classList.remove('car-search-active');
      }
    }
  };

  // Handle waiting state change from header
  const handleWaitingStateChange = (isWaiting) => {
    setIsWaitingForOperator(isWaiting);
  };

  // Handle CTA click in the footer
  const handleCTAClick = (ctaType) => {
    // Just dispatch the event without closing/opening the chat:
    const ctaEvent = new CustomEvent('ctaButtonClicked', {
      detail: { ctaType }
    });
    window.dispatchEvent(ctaEvent);
  };

  // Simplified input disabled logic
  const isInputDisabled = (() => {
    console.log('🔒 ChatBody: Checking if input should be disabled:', {
      showMandatoryForm,
      mandatoryForm,
      automaticHumanControl
    });
    
    // SIMPLIFIED: Just disable when showing mandatory form
    // We'll rebuild the proper offline workflow logic later
    return showMandatoryForm;
  })();

  return (
    <div 
      className={`mia-chat-container ${isChatWide ? 'wide' : ''} ${isCarSearchOpen ? 'mia-search-panel-open' : ''}`}
      onClick={handleUserInteraction}
    >
      <ChatHeader 
        isMobile={isMobile}
        toggleChat={toggleChat}
        toggleChatWidth={toggleChatWidth}
        isChatWide={isChatWide}
        isLoading={isLoading}
        onSendMessage={onSendMessage}
        isHumanControlled={isHumanControlled}
        humanControlMessage={humanControlMessage}
        controller={controller}
        isOnline={isConnected}
        toggleSearchPanel={toggleSearchPanel}
        onCarSearchOpen={handleCarSearchOpen}
        onCarSearchClose={handleCarSearchClose}
        openedByTrigger={openedByTrigger}
        isCompactMode={isCompactMode}
        toggleCompactMode={toggleCompactMode}
        onWaitingStateChange={handleWaitingStateChange}
        isConnected={isConnected}
        isAuthenticated={isAuthenticated}
        statusReceived={statusReceived}
      />
      
      <div 
        className={`mia-chat-messages ${areQuestionsVisible ? 'questions-visible' : ''} ${isFormFocused ? 'form-field-focused' : ''}`} 
        ref={messagesContainerRef}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        style={{
          transform: pullDistance > 0 ? `translateY(${pullDistance}px)` : 'none',
          transition: pullDistance === 0 ? 'transform 0.3s cubic-bezier(0.2, 0, 0, 1)' : 'none'
        }}
      >
        {/* Pull-to-refresh indicator */}
        {pullDistance > 0 && (
          <div 
            className="mia-pull-refresh-indicator"
            style={{
              opacity: Math.min(1, pullDistance / PULL_THRESHOLD),
              transform: `translateY(${-pullDistance}px)`
            }}
          >
            <div className={`mia-refresh-icon ${pullDistance >= PULL_THRESHOLD ? 'ready' : ''} ${isRefreshing ? 'refreshing' : ''}`}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M23 4v6h-4M1 20v-6h4M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
              </svg>
            </div>
            <span>{isRefreshing ? t('refreshing') : pullDistance >= PULL_THRESHOLD ? t('releaseToRefresh') : t('pullToRefresh')}</span>
          </div>
        )}
        {memoizedMessages}
        {(isLoading || showWelcomeLoading) && (
          <div 
            className={`mia-message mia-bot-message mia-loading-message ${isHumanControlled || showWelcomeLoading ? 'mia-human-operator-typing' : 'mia-ai-typing'}`}
            ref={loadingIndicatorRef}
            tabIndex={0}
            aria-label={isHumanControlled || showWelcomeLoading ? t('operatorTyping') : t('assistantTyping')}
          >
            {(isHumanControlled && !showWelcomeLoading) ? (
              <div className="mia-operator-typing">
                <span>{t('operatorTyping')}</span>
                <span className="mia-loading-dot"></span>
                <span className="mia-loading-dot"></span>
                <span className="mia-loading-dot"></span>
              </div>
            ) : showWelcomeLoading ? (
              <div className="mia-operator-typing">
                <span>{t('operatorConnecting')}</span>
                <span className="mia-loading-dot"></span>
                <span className="mia-loading-dot"></span>
                <span className="mia-loading-dot"></span>
              </div>
            ) : (
              <div 
                className="mia-ai-typing-container" 
                data-tool={messages[messages.length - 1]?.tool?.[0] || ''}
                data-loading-state={
                  toolProgress < 20 ? 'initializing' :
                  toolProgress < 95 ? 'processing' :
                  'completing'
                }
              >
                <div className="mia-ai-typing-animation whatsapp-style">
                  <span className="mia-loading-dot"></span>
                  <span className="mia-loading-dot"></span>
                  <span className="mia-loading-dot"></span>
                </div>
                
                {/* Always show tool indicator and text when tool exists */}
                {messages[messages.length - 1]?.tool && (
                  <>
                    <div 
                      className="mia-tool-indicator"
                      data-progress={Math.floor(toolProgress / 25) * 25}
                    >
                      <CircularProgress 
                        className="mia-tool-indicator-progress"
                        variant="determinate" 
                        value={toolProgress} 
                        size={32}
                        thickness={3}
                        style={{ 
                          color: toolProgress >= 95 ? '#4caf50' : 'var(--mia-primary-color, #1976d2)',
                          transition: 'color 0.3s ease'
                        }}
                      />
                      <div className="mia-tool-indicator-icon">
                        {getToolIcon(messages[messages.length - 1].tool)}
                      </div>
                    </div>
                    
                    <span className="mia-tool-status-text">
                      {getToolDisplay(messages[messages.length - 1].tool)}
                    </span>
                  </>
                )}
              </div>
            )}
          </div>
        )}
        
        {renderQuickSuggestions()}
      </div>
      
      <ChatFooter 
        onSendMessage={handleSendMessage}
        onSendFile={onSendFile}
        onSendAudio={onSendAudio}
        isLoading={isLoading}
        isHumanControlActive={isHumanControlled}
        inputValue={inputValue}
        setInputValue={setInputValue}
        predictedQuestions={processedPredictedQuestions}
        onQuestionClick={handleQuestionClick}
        isMobile={isMobile}
        onQuestionsVisibilityChange={handleQuestionsVisibilityChange}
        isCarSearchOpen={isCarSearchOpen}
        areQuestionsVisible={areQuestionsVisible}
        setAreQuestionsVisible={setAreQuestionsVisible}
        isCompactMode={isCompactMode}
        openedByTrigger={openedByTrigger}
        ref={inputRef}
        onCTAClick={handleCTAClick}
        disabled={isInputDisabled}
        isWaitingForOperator={isWaitingForOperator}
        isConnected={isConnected}
        isAuthenticated={isAuthenticated}
        statusReceived={statusReceived}
        inlineSearchActions={inlineSearchActions}
        replyMessage={replyMessage}
        onClearReply={() => {
          setReplyMessage(null);
          // Dispatch event to clear reply in other components
          document.dispatchEvent(new CustomEvent('clearReply'));
        }}
      />
      
      {/* Add MobileCarSearch component here */}
      {showCarSearch && (
        <MobileCarSearch 
          onClose={handleCarSearchClose}
          onSubmit={handleCarSearchSubmit}
          toggleChat={toggleChat}
        />
      )}
    </div>
  );
});

export default ChatBody; 
